/**
 * InlineTextEditor - obvious click-to-edit text with a popover input.
 *
 * Jul 17 - shared by the bank feed's Contact Name (table + cards) and card
 * Description editors. The affordance is deliberately visible: dashed
 * underline + always-visible pencil + hover tint, so users KNOW it's
 * editable without hunting.
 */
import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Edit3, Check, X, Loader2 } from 'lucide-react';

export const InlineTextEditor = ({
  value,
  placeholder = 'Add…',
  label = 'Edit',
  onSave,            // async (newValue) => void
  maxLength = 200,
  truncateAt = 60,
  textClass = 'text-xs text-foreground font-medium',
  quoted = false,
  testIdPrefix,
}) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(value || ''); }, [value]);

  const commit = async () => {
    const next = (val || '').trim();
    if (!next || next === (value || '').trim()) { setOpen(false); return; }
    setSaving(true);
    try { await onSave?.(next); setOpen(false); } catch { /* toast upstream */ } finally { setSaving(false); }
  };

  const shown = value
    ? (value.length > truncateAt ? `${value.slice(0, truncateAt)}…` : value)
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group/ite inline-flex items-center gap-1 max-w-full text-left rounded px-0.5 -mx-0.5
            border-b border-dashed border-slate-300 hover:border-[#108981] hover:bg-[#108981]/5
            focus:outline-none focus:ring-1 focus:ring-[#108981]/40 transition-colors"
          title={`Click to edit ${label.toLowerCase()}`}
          data-testid={testIdPrefix ? `${testIdPrefix}-edit` : undefined}
        >
          <span className={`truncate ${value ? textClass : 'text-xs text-muted-foreground italic'}`}>
            {quoted && value ? `“${shown}”` : shown}
          </span>
          <Edit3 className="w-3 h-3 shrink-0 text-slate-400 group-hover/ite:text-[#108981]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" side="bottom" align="start">
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Edit {label.toLowerCase()}</p>
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          maxLength={maxLength}
          className="h-8 text-xs"
          autoFocus
          placeholder={placeholder}
          data-testid={testIdPrefix ? `${testIdPrefix}-input` : undefined}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setOpen(false); }}
        />
        <div className="flex justify-end gap-1 mt-2">
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setOpen(false)} disabled={saving}>
            <X className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            className="h-7 px-3 bg-[#108981] hover:bg-[#0d6f68]"
            onClick={commit}
            disabled={saving}
            data-testid={testIdPrefix ? `${testIdPrefix}-save` : undefined}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InlineTextEditor;
